import { FC, useMemo, ReactNode } from 'react';
import { formatDistance } from 'date-fns/esm';
import { Tooltip } from '@robocorp/components';
import { formatTimestamp, formatVerboseTimestamp, getDateLocale } from '~/utils/timeHelper';
import { TimestampFormatOptions } from '~/types';
import { useCurrentOrganization } from '~/queries';
import { getTimezoneValue, LOCAL_TZ_VALUE } from '~/utils/timezoneHelpers';

type DateType = Date | string | number;

interface ComponentProps {
  value: DateType;
  raw?: boolean;
  hideTooltip?: boolean;
}

type ZonedProps = TimestampFormatOptions & ComponentProps;
type Props = TimestampFormatOptions & Partial<ComponentProps>;

const getCurrentOffset = () => new Date().getTimezoneOffset();
const getIsLocalTimezone = (timezone?: string, offset?: number) => {
  if (timezone === undefined || offset === undefined) return true;
  if (timezone === LOCAL_TZ_VALUE) return true;
  if (offset === getCurrentOffset()) return true;
  return false;
};

const formatter = (value: DateType, options: TimestampFormatOptions, raw = false) => {
  if (raw) return formatTimestamp(value, { locale: options.locale, timezone: options.timezone });
  return formatVerboseTimestamp(value, options);
};

const Container: FC<{ children: ReactNode; label?: string }> = ({ children, label }) => {
  if (label) {
    return (
      <Tooltip text={label}>
        <span>{children}</span>
      </Tooltip>
    );
  }

  return <span>{children}</span>;
};

/**
 * Formats timestamp using zone time settings
 */
const ZonedTimestamp: FC<ZonedProps> = ({
  value,
  raw,
  timezone: timezonePreset,
  hideTooltip,
  ...formatOptions
}) => {
  const { timezone, label } = useMemo(() => {
    if (!timezonePreset) return {};

    const details = getTimezoneValue(timezonePreset) ?? {};
    return {
      timezone: details.value,
      label:
        hideTooltip || getIsLocalTimezone(details.value, details.offset)
          ? undefined
          : details.label,
    };
  }, [timezonePreset, hideTooltip]);

  return (
    <Container label={label}>{formatter(value, { ...formatOptions, timezone }, raw)}</Container>
  );
};

/**
 * Fallback to handle empty timestamps
 */
const BaseTimestamp: FC<Props> = ({ value, ...props }) =>
  value ? <ZonedTimestamp value={value} {...props} /> : null;

/**
 * Format timestamp using browser settings:
 * - used for places where organizaiton settings don't apply, like user settings
 * - otherwise some oddities can be encountered where timestamps in eg. user settings keep changing from previous org data
 */
const LocalTimestamp: FC<Omit<Props, 'timezone' | 'timeFormat'>> = BaseTimestamp;

/**
 * Uses time settings from current organization to format timestamp, included settings:
 * - organization timezone
 * - organization time format
 */
const OrganizationTimestamp: FC<Props> = (props) => {
  const { data: currentOrganization } = useCurrentOrganization();

  return (
    <BaseTimestamp
      timezone={currentOrganization?.details?.timezone}
      timeFormat={currentOrganization?.details?.timeFormat}
      {...props}
    />
  );
};

/**
 * Returns difference between two dates in words
 */
const TimestampDiff: FC<{
  from: DateType;
  to?: DateType;
  locale?: TimestampFormatOptions['locale'];
}> = ({ from, to, locale }) =>
  from ? (
    <Tooltip text={<OrganizationTimestamp value={from} locale={locale} hideTooltip year seconds />}>
      <span>
        {formatDistance(new Date(from), to ? new Date(to) : new Date(), {
          locale: getDateLocale(locale),
          addSuffix: true,
        })}
      </span>
    </Tooltip>
  ) : null;

export const Timestamp = OrganizationTimestamp as typeof OrganizationTimestamp & {
  Local: typeof LocalTimestamp;
  Diff: typeof TimestampDiff;
};

Timestamp.Local = LocalTimestamp;
Timestamp.Diff = TimestampDiff;
